import { useEffect, useState } from 'react';
import { AirplaneLanding, AirplaneTakeoff, X } from '@phosphor-icons/react';
import { useStore } from '../state/store';
import type { AirportBoard, BoardFlight } from '../types';
import { formatEtaClock } from '../flightProgress';
import { formatAltitude } from '../map/altitude';
import { apiUrl } from '../api';

/** Refresh cadence for the open board. The underlying traffic updates ~2 s, but a
 *  departures list doesn't need to churn that fast and each call re-scans all watchers. */
const REFRESH_MS = 15_000;

function statusLabel(f: BoardFlight): string {
  switch (f.status) {
    case 'ground':
      return 'On ground';
    case 'approaching':
      return 'Approaching';
    case 'departing':
      return 'Departing';
    default:
      return 'En route';
  }
}

function FlightRow({ flight, kind }: { flight: BoardFlight; kind: 'arrival' | 'departure' }) {
  const select = useStore((s) => s.select);
  const eta = formatEtaClock(flight.etaMs);
  return (
    <li>
      <button onClick={() => select(flight.icao24)} title="Show this flight">
        <span className="cs mono">{flight.callsign ?? flight.icao24.toUpperCase()}</span>
        <span className="pair mono">{flight.otherIcao ?? '----'}</span>
        <span className="eta mono">
          {kind === 'arrival' && eta ? eta : `${Math.round(flight.distanceKm).toLocaleString()} km`}
        </span>
        <span className={`status ${flight.status}`}>{statusLabel(flight)}</span>
        <span className="alt mono">{formatAltitude(flight.altitudeFt, flight.onGround)}</span>
      </button>
    </li>
  );
}

/**
 * Arrivals/departures board for the airport clicked on the map. Only flights
 * currently tracked with a known (cached) route appear. There's no schedule
 * feed behind this, so it's "what's actually flying to/from here right now",
 * not a terminal display. See server/src/airportBoard.ts.
 */
export default function AirportPanel() {
  const ident = useStore((s) => s.selectedAirport);
  const selectAirport = useStore((s) => s.selectAirport);
  const [board, setBoard] = useState<AirportBoard | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'arrivals' | 'departures'>('arrivals');

  useEffect(() => {
    if (!ident) {
      setBoard(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const load = () =>
      fetch(apiUrl(`/api/airport/${encodeURIComponent(ident)}`))
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
        .then((d: AirportBoard) => {
          if (!cancelled) setBoard(d);
        })
        .catch(() => {
          if (!cancelled) setError("Couldn't load this airport. Retrying in 15 seconds.");
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    void load();
    const iv = setInterval(() => void load(), REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [ident]);

  if (!ident) return null;

  const list = board ? (tab === 'arrivals' ? board.arrivals : board.departures) : [];
  const kind = tab === 'arrivals' ? 'arrival' : 'departure';

  return (
    <aside className="airport-panel side-panel glass" data-testid="airport-panel" aria-label="Airport board">
      <header className="ap-head">
        <div className="ap-id">
          <span className="ap-code mono">
            {board?.airport.ident ?? ident}
            {board?.airport.iata && <span className="ap-iata">{board.airport.iata}</span>}
          </span>
          <h2 className="ap-name">
            {board ? board.airport.name : <span className="skeleton skeleton-line" style={{ width: '12em' }} />}
          </h2>
          {board?.airport.municipality && <span className="ap-place">{board.airport.municipality}</span>}
        </div>
        <button className="icon-btn" onClick={() => selectAirport(null)} aria-label="Close" title="Close">
          <X size={17} weight="bold" />
        </button>
      </header>

      <div className="segmented ap-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={tab === 'arrivals'}
          className={tab === 'arrivals' ? 'active' : ''}
          onClick={() => setTab('arrivals')}
        >
          <AirplaneLanding size={15} weight="bold" aria-hidden="true" />
          Arrivals <span className="mono tab-count">{board?.arrivals.length ?? 0}</span>
        </button>
        <button
          role="tab"
          aria-selected={tab === 'departures'}
          className={tab === 'departures' ? 'active' : ''}
          onClick={() => setTab('departures')}
        >
          <AirplaneTakeoff size={15} weight="bold" aria-hidden="true" />
          Departures <span className="mono tab-count">{board?.departures.length ?? 0}</span>
        </button>
      </div>

      <div className="board-head" aria-hidden="true">
        <span>Flight</span>
        <span>{tab === 'arrivals' ? 'From' : 'To'}</span>
        <span>{tab === 'arrivals' ? 'ETA' : 'Distance'}</span>
      </div>

      {loading && !board && (
        <ul className="board is-loading" aria-busy="true">
          {[0, 1, 2, 3].map((i) => (
            <li key={i}>
              <span className="skeleton skeleton-row" />
            </li>
          ))}
        </ul>
      )}
      {error && <p className="panel-error">{error}</p>}
      {board && list.length === 0 && !loading && (
        <div className="board-empty">
          {tab === 'arrivals' ? (
            <AirplaneLanding size={22} weight="duotone" aria-hidden="true" />
          ) : (
            <AirplaneTakeoff size={22} weight="duotone" aria-hidden="true" />
          )}
          <p>
            No tracked {tab} right now. Flights only show up here once they're airborne nearby with a known route,
            since there's no schedule feed behind this board.
          </p>
        </div>
      )}
      {list.length > 0 && (
        <ul className="board">
          {list.map((f) => (
            <FlightRow key={f.icao24} flight={f} kind={kind} />
          ))}
        </ul>
      )}
      {board && board.onGroundCount > 0 && (
        <p className="ap-ground">
          <span className="mono">{board.onGroundCount}</span> tracked aircraft on the ground nearby
        </p>
      )}
    </aside>
  );
}
